import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Radio, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";

/** "Connect Genesys" / "Disconnect" toggle that drives the PKCE agent flow.
 *  Opens the Genesys /authorize URL in a popup, listens for the postMessage
 *  from the /genesys/callback page, then exchanges code → tokens on the
 *  backend. Emits `onChange(connected)` so parents can re-check status.
 */
export default function GenesysConnectButton({ configured = true, onChange }) {
  const [connected, setConnected] = useState(false);
  const [expiresIn, setExpiresIn] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/genesys/agent/status");
      setConnected(!!data?.connected);
      setExpiresIn(data?.expires_in ?? null);
      if (onChange) onChange(!!data?.connected);
    } catch (_) {
      setConnected(false);
      if (onChange) onChange(false);
    }
  }, [onChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const connect = async () => {
    if (busy) return;
    setBusy(true);
    let popup;
    try {
      const redirect_uri = `${window.location.origin}/genesys/callback`;
      const { data } = await api.get("/genesys/agent/authorize-url", { params: { redirect_uri } });
      popup = window.open(data.authorize_url, "genesys_oauth", "width=520,height=680");
      if (!popup) {
        toast.error("Popup blocked. Allow popups for this site and retry.");
        setBusy(false);
        return;
      }
      const state = data.state;
      const listener = async (ev) => {
        if (!ev?.data || ev.data.kind !== "genesys-oauth-callback") return;
        if (ev.origin !== window.location.origin) return;
        window.removeEventListener("message", listener);
        if (ev.data.error) {
          toast.error(`Genesys sign-in cancelled: ${ev.data.error_description || ev.data.error}`);
          setBusy(false);
          return;
        }
        if (!ev.data.code || ev.data.state !== state) {
          toast.error("Genesys callback failed (state mismatch).");
          setBusy(false);
          return;
        }
        try {
          await api.post("/genesys/agent/exchange", { code: ev.data.code, state });
          toast.success("Connected to Genesys.");
          await refresh();
        } catch (e) {
          const detail = e?.response?.data?.detail || "Token exchange failed";
          toast.error(`Genesys exchange failed: ${detail}`);
        } finally {
          setBusy(false);
        }
      };
      window.addEventListener("message", listener);

      // If the popup is closed before postMessage arrives, tidy up.
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          window.removeEventListener("message", listener);
          setBusy(false);
        }
      }, 500);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to start Genesys sign-in";
      const status = e?.response?.status;
      if (status === 424) toast.error(`Genesys not configured — ${detail}`);
      else toast.error(detail);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/genesys/agent/disconnect");
      toast.success("Disconnected from Genesys.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  if (!configured) {
    return null;   // Tenant hasn't set creds → no button
  }

  if (connected) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-7 px-2 text-[11px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
        onClick={disconnect}
        disabled={busy}
        data-testid="genesys-disconnect-btn"
        title={expiresIn ? `Session valid for ${Math.max(0, Math.floor(expiresIn / 60))} min` : ""}
      >
        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <LogOut className="w-3 h-3 mr-1" />}
        Connected
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 px-2 text-[11px] border-indigo-200 text-indigo-700 hover:bg-indigo-50"
      onClick={connect}
      disabled={busy}
      data-testid="genesys-connect-btn"
    >
      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Radio className="w-3 h-3 mr-1" />}
      Connect Genesys
    </Button>
  );
}
